import { useEffect, useRef, useState } from 'react';

const timelineData = [
  {
    era: '过去',
    eraEn: 'PAST',
    color: '#64748B',
    dotColor: '#64748B',
    title: '人力堆积时代',
    desc: '软件开发依赖人力堆积。需求越多，人越多；项目越大，成本越高。交付周期长，试错成本高，大量想法因为"做不起"而被放弃。',
    tags: ['人力密集', '高成本', '长周期'],
  },
  {
    era: '现在',
    eraEn: 'NOW',
    color: '#2563EB',
    dotColor: '#2563EB',
    title: 'AI 协作生产',
    desc: 'AI 开始参与需求分析、编码、测试、文档全流程。执行成本快速塌缩，过去需要一个团队的工作，现在可以用更小的团队完成。',
    tags: ['AI 协作', '成本压缩', '效率提升'],
  },
  {
    era: '未来',
    eraEn: 'FUTURE',
    color: '#F97316',
    dotColor: '#F97316',
    title: '交付速度即壁垒',
    desc: '交付速度将成为核心竞争壁垒。能快速验证想法、快速迭代产品的企业，将在竞争中建立不可逾越的优势。',
    tags: ['速度壁垒', '持续迭代', '能力沉淀'],
  },
];

export default function TimelineSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="text-center mb-20">
          <span className="text-xs uppercase tracking-widest text-[#64748B] mb-4 block" style={{ fontFamily: 'Inter, sans-serif' }}>
            The Shift
          </span>
          <h2
            className="text-3xl md:text-5xl font-bold text-[#0F172A] leading-tight"
            style={{ fontFamily: '\'Noto Sans SC\', sans-serif', letterSpacing: '-0.02em' }}
          >
            执行成本正在快速塌缩
          </h2>
        </div>

        {/* Timeline connector (desktop) */}
        <div className="hidden md:flex items-center justify-between mb-8 relative">
          <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-px bg-slate-200 mx-16" />
          {timelineData.map((item, idx) => (
            <div key={idx} className="relative z-10 flex flex-col items-center" style={{ width: '33%' }}>
              <div
                className="w-4 h-4 rounded-full border-4 border-white transition-all duration-700"
                style={{
                  backgroundColor: item.dotColor,
                  boxShadow: visible ? `0 0 0 6px ${item.dotColor}20` : 'none',
                  transform: visible ? 'scale(1)' : 'scale(0)',
                  transitionDelay: `${idx * 200}ms`,
                }}
              />
            </div>
          ))}
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {timelineData.map((item, idx) => (
            <div
              key={idx}
              className="bg-[#F8FAFC] rounded-2xl p-8 md:p-10 border border-slate-100 transition-all duration-700"
              style={{
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(30px)',
                transitionDelay: `${idx * 150 + 200}ms`,
              }}
            >
              <div className="flex items-center gap-3 mb-6">
                <span
                  className="text-xs font-bold uppercase tracking-widest px-2.5 py-1 rounded-full"
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    color: item.color,
                    backgroundColor: `${item.color}15`,
                  }}
                >
                  {item.eraEn}
                </span>
                <span className="text-sm text-[#64748B]" style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}>
                  {item.era}
                </span>
              </div>
              <h3
                className="text-xl font-bold text-[#0F172A] mb-4"
                style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
              >
                {item.title}
              </h3>
              <p
                className="text-sm text-[#64748B] leading-relaxed mb-6"
                style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
              >
                {item.desc}
              </p>
              <div className="flex flex-wrap gap-2">
                {item.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs px-3 py-1 rounded-full border"
                    style={{
                      fontFamily: '\'Noto Sans SC\', sans-serif',
                      color: item.color,
                      borderColor: `${item.color}30`,
                      backgroundColor: `${item.color}08`,
                    }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
